import { useEffect, useState } from "react";
import { ApiError, PayrollValidationError, projectAnnualFuta } from "@/api/client";
import type { AnnualFutaForm, PayrollEmployee } from "@/api/types";
import { PAY_PERIODS_PER_YEAR, TAX_YEAR } from "@/pages/payroll-fixtures";

export interface UseAnnualFutaResult {
  status: "idle" | "loading" | "ready" | "error";
  form: AnnualFutaForm | null;
  error: string | null;
}

/**
 * Projects a roster into IRS Form 940 — the annual federal unemployment return.
 *
 * Takes whichever business is selected, because unlike the 943/941 split EVERY employer
 * files a 940. The H-2A exemption still applies: those wages are exempt from FUTA as well
 * as FICA, so the same people drop out of this return for a second, independent statutory
 * reason.
 */
export function useAnnualFuta(employees: PayrollEmployee[] | null): UseAnnualFutaResult {
  const [status, setStatus] = useState<UseAnnualFutaResult["status"]>("idle");
  const [form, setForm] = useState<AnnualFutaForm | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (employees === null) {
      setStatus("loading");
      return;
    }
    if (employees.length === 0) {
      setStatus("ready");
      setForm(null);
      return;
    }

    const controller = new AbortController();
    setStatus("loading");
    setError(null);

    projectAnnualFuta(
      {
        payPeriodsToProject: PAY_PERIODS_PER_YEAR,
        employees: employees.map((employee) => ({
          employeeId: employee.id,
          grossWages: employee.grossWages,
          payPeriodsPerYear: PAY_PERIODS_PER_YEAR,
          taxYear: TAX_YEAR,
          filingStatus: "Single",
          // The roster's H-2A badge drives the FUTA exemption exactly as it drives the
          // FICA one — the endpoint maps ficaExempt onto FutaExempt, with the citation
          // on Form940EmployeeWages.
          ficaExempt: employee.ficaExemptByLaw,
        })),
      },
      controller.signal,
    )
      .then((result) => {
        setForm(result);
        setStatus("ready");
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          cause instanceof PayrollValidationError
            ? Object.values(cause.errors).flat().join(" ")
            : cause instanceof ApiError
              ? cause.message
              : "Could not project the unemployment return.",
        );
        setStatus("error");
      });

    return () => controller.abort();
  }, [employees]);

  return { status, form, error };
}
